import { formatDistance, isPast } from "date-fns";
import { SharedReportOptions } from "Types";
import { SharedReportInfo } from "../../../../types";
import { getSharedReportExportSettings } from "../../../../utils";

type ReportSettingsListProps = {
  sharedReportOptions: SharedReportOptions;
  expiresIn?: number;
  expirationDate?: string;
  sharedBy?: SharedReportInfo["user"];
  sharedDate?: string;
  resentToEmailAddressesCount?: number;
  sentToEmailAddressesCount?: number;
};

const getRelativeDate = (date: string): string =>
  formatDistance(new Date(date), new Date(), { addSuffix: true });

const getExpiration = (expiresIn?: number, expirationDate?: string): string => {
  if (expiresIn) {
    return `Expires in ${expiresIn} day${expiresIn > 1 ? "s" : ""}`;
  }

  if (!expirationDate) return "";

  return isPast(new Date(expirationDate))
    ? `Expired ${getRelativeDate(expirationDate)}`
    : `Expires ${getRelativeDate(expirationDate)}`;
};

const getEmailAddresses = (count: number, areNew = false): string =>
  `${count} ${areNew ? "new " : ""}e-mail address${count > 1 ? "es" : ""}`;

const getNotification = (
  passwordProtected: boolean,
  resentToEmailAddressesCount: number,
  sentToEmailAddressesCount: number,
): string => {
  const targets = [
    resentToEmailAddressesCount > 0 &&
      `resent to ${getEmailAddresses(resentToEmailAddressesCount)}`,
    sentToEmailAddressesCount > 0 &&
      `sent to ${getEmailAddresses(
        sentToEmailAddressesCount,
        resentToEmailAddressesCount > 0,
      )}`,
  ]
    .filter(Boolean)
    .join(" and ");

  if (!targets) return "";

  return `Report link${
    passwordProtected ? " and password" : ""
  } will be ${targets}`;
};

const getReportSettingsList = ({
  sharedReportOptions,
  expiresIn,
  expirationDate,
  sharedBy,
  sharedDate,
  resentToEmailAddressesCount = 0,
  sentToEmailAddressesCount = 0,
}: ReportSettingsListProps): Record<string, string> => {
  const { enabledExports, disabledExports } = getSharedReportExportSettings({
    cve: sharedReportOptions.rl_cve_export_enabled,
    cycloneDX: sharedReportOptions.cyclonedx_export_enabled,
    json: sharedReportOptions.rl_json_export_enabled,
    safe: sharedReportOptions.rl_safe_export_enabled,
    sarif: sharedReportOptions.sarif_export_enabled,
    spdx: sharedReportOptions.spdx_export_enabled,
    summaryPdf: sharedReportOptions.rl_summary_pdf_export_enabled,
    uri: sharedReportOptions.rl_uri_export_enabled,
  });

  const notification = getNotification(
    sharedReportOptions.password_protected,
    resentToEmailAddressesCount,
    sentToEmailAddressesCount,
  );

  return {
    ...(sharedDate
      ? {
          "Shared by": `${
            sharedBy
              ? `${sharedBy.first_name} ${sharedBy.last_name}`
              : "Removed user"
          }, ${getRelativeDate(sharedDate)}`,
        }
      : {}),
    "Link expiration": getExpiration(expiresIn, expirationDate),
    "Package download": sharedReportOptions.package_download_enabled
      ? "Enabled"
      : "Disabled",
    ...(enabledExports.length > 0
      ? { "Exports enabled": enabledExports.join(", ") }
      : {}),
    ...(disabledExports.length > 0
      ? { "Exports disabled": disabledExports.join(", ") }
      : {}),
    "Password protection": sharedReportOptions.password_protected
      ? "Enabled — anyone with the link and password can view the latest report"
      : "Disabled — anyone with the link can view the latest report",
    ...(notification ? { "Notification e-mail": notification } : {}),
  };
};

export default getReportSettingsList;
